import * as React from "react";
import styled from "styled-components";
import { useImageLoaded } from "../hooks/useImageLoaded";
import { skinTones, tEmoji, tSkinTone } from "@floji-internal/noto-emoji";
import { emojiShortcutRegex, EmojiSize, getDefaultBase, getEmojiSize, getImageFromEmoji } from "./utils";
import { StyledEmoji } from "./StyledEmoji";
import { emojiRegex, RegExpFlags } from "--root/utils/regex";
import { useEmojiList } from "../../client/web/src/common/hooks/useEmojiList";

type EmojiProps = {
    emoji: string | tEmoji                    // Emoji character or shortcut to render    
    baseUrl?: string                        // Base url to load emojis from
    disallowFallback?: boolean              // Allow fallback to system emoji
    size?: EmojiSize

    children?: never;
} & React.ImgHTMLAttributes<HTMLImageElement>



const EmojiFallback = styled.span<{ size: string }>`
    font-size: ${p => p.size};
`;

export const EmojiSizeContext = React.createContext<EmojiProps["size"] | null>(null);


const Emoji: React.FC<EmojiProps> = (props) => {
    const { emoji, disallowFallback,
        size = "inline",
        baseUrl = getDefaultBase(),
        ...containerProps } = props;

    // Currently we call RegisterCustomElement with a fixed size and that is used for all slate inputs, this is my hacky way
    // around that issue without having to dig too deep and refactor anything. 
    const ctxSize = React.useContext(EmojiSizeContext) || size;
    const emojiList = useEmojiList();

    // Todo: oversizeOffset assumes a 1.3em line height. This should probably take this as a prop or from theme
    // as this will likely break down if we change text styling in the future.
    const { imageSize, containerSize, oversizeOffset } = getEmojiSize(ctxSize);

    let emojiChar: string = "";
    let emojiName: string = "emoji";
    if (typeof emoji == "string") {
        // We should either have a unicode emoji or a shortcut...
        const parts = /(:[^:\s]*:)(:[^:\s]*:)?/.exec(emoji);

        if (parts == null) {
            // Not a shortcut, treat it as if it where a unicode character for now
            emojiChar = emoji;

        } else {
            // Probably a shortcut try to set emoji by shortcut
            const shortcut = parts[1]!;
            const skinTone = parts[2];
            const foundEmoji = emojiList?.emojisByShortcut[shortcut]!;
            if (!foundEmoji) {
                console.log("No emoji:", shortcut);
                return null;
            }
            emojiChar = foundEmoji.emoji;
            emojiName = foundEmoji.name;

            // Check to see if we have a skin tone
            if (skinTone && foundEmoji.tones) {
                let toneKey = Object.keys(skinTones).find(key => skinTones[key].shortcut == skinTone) as tSkinTone | undefined;
                let tone = toneKey && foundEmoji.tones[toneKey];
                if (tone) {
                    emojiChar = tone.emoji;
                }
            }
        }

    } else {
        // We have an tEmoji object
        emojiName = emoji.name || emoji.shortcut;
        emojiChar = emoji.emoji;
    }
    const emojiSrc = React.useMemo(() => getImageFromEmoji(emojiChar, imageSize as any, baseUrl), [emojiChar, imageSize, baseUrl]);
    const isLoaded = useImageLoaded(emojiSrc || undefined);
    // If we have a src and it is loaded/loading render the image
    if ((isLoaded || isLoaded === undefined) && emojiSrc)
        return <StyledEmoji src={emojiSrc} alt={emojiName} size={containerSize} offset={oversizeOffset} {...containerProps} />

    // Else try the fallback and render the OS/Browser default
    if (disallowFallback) return null;
    return <EmojiFallback size={containerSize} {...containerProps as any}>{emojiChar}</EmojiFallback>
}


/**
 * Return array of react nodes with emojis replaced with the appropriate emoji element
 * @param value String to replace emojis in
 */
export const replaceEmojis = (value: string, size?: EmojiProps["size"]): Array<React.ReactNode> => {
    const myEmojiRegexp = emojiRegex(RegExpFlags.global | RegExpFlags.multiLine)
    const combinedRegex = `${emojiShortcutRegex.source}|${myEmojiRegexp.source}`
    const regexp = new RegExp(combinedRegex, myEmojiRegexp.flags);
    let children: Array<React.ReactNode> = [];
    let lastIdx: number = 0;
    let match: RegExpExecArray | null;
    while (match = regexp.exec(value)) {
        if (match.index !== lastIdx) children.push(value.slice(lastIdx, match.index));
        children.push(<Emoji emoji={match[0]!} size={size} />);
        lastIdx = match.index + match[0]!.length;
    }
    if (lastIdx !== value.length) children.push(value.slice(lastIdx, value.length));
    return children;
}

export default Emoji;
export { Emoji, EmojiProps };